import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import toast from 'react-hot-toast';
import {
  Brain,
  LayoutDashboard,
  MessageSquare,
  Calendar,
  FileText,
  Users,
  Settings,
  CreditCard,
  ClipboardCheck,
  X,
  CheckSquare,
  LogOut,
  BookOpen
} from 'lucide-react';

interface SidebarProps {
  onClose?: () => void;
}

export default function Sidebar({ onClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  const menuItems = [
    { icon: <LayoutDashboard size={20} />, label: 'Dashboard', path: '/dashboard' },
    { icon: <MessageSquare size={20} />, label: 'Messages', path: '/messages' },
    { icon: <Calendar size={20} />, label: 'Calendar', path: '/calendar' },
    { icon: <CheckSquare size={20} />, label: 'Tasks', path: '/tasks' },
    { icon: <ClipboardCheck size={20} />, label: 'Attendance', path: '/attendance' },
    { icon: <FileText size={20} />, label: 'Documents', path: '/documents' },
    { icon: <Users size={20} />, label: 'Team', path: '/team' },
  ];

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
      onClose?.();
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out');
    }
  };

  return (
    <div className="w-64 h-full bg-surface-light border-r border-border-light flex flex-col">
      {/* Logo */}
      <div className="p-4 border-b border-border-light flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Brain className="w-8 h-8 text-primary-500" />
          <span className="font-bold text-lg text-text-primary">Campus Connect</span>
        </Link>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 -ml-2 text-text-secondary hover:text-text-primary lg:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.label}>
                <Link
                  to={item.path}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-500 text-white'
                      : 'text-text-secondary hover:bg-primary-50 hover:text-primary-600'
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
          
          {/* Separator */}
          <li className="pt-4">
            <hr className="border-border-light" />
          </li>

          {/* Help & Settings */}
          <li>
            <Link
              to="/getting-started"
              onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                location.pathname === '/getting-started'
                  ? 'bg-primary-500 text-white'
                  : 'text-text-secondary hover:bg-primary-50 hover:text-primary-600'
              }`}
            >
              <BookOpen size={20} />
              <span>Getting Started</span>
            </Link>
          </li>
          <li>
            <Link
              to="/settings"
              onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                location.pathname === '/settings'
                  ? 'bg-primary-500 text-white'
                  : 'text-text-secondary hover:bg-primary-50 hover:text-primary-600'
              }`}
            >
              <Settings size={20} />
              <span>Settings</span>
            </Link>
          </li>
          <li>
            <Link
              to="/upgrade"
              onClick={onClose}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-white bg-primary-500 
                       hover:bg-primary-600 transition-colors"
            >
              <CreditCard size={20} />
              <span>Upgrade Plan</span>
            </Link>
          </li>
          
          {/* Logout Button */}
          <li className="pt-4">
            <hr className="border-border-light" />
          </li>
          <li>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-red-600 
                       hover:bg-red-50 hover:text-red-700 transition-colors w-full"
            >
              <LogOut size={20} />
              <span>Log Out</span>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
}